<script setup>
import { computed, onMounted, ref, watch } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';
import { useAlert } from 'dashboard/composables';
import AnswerTemplateTab from './product/AnswerTemplateTab.vue';

const props = defineProps({
  assistant: {
    type: Object,
    default: () => ({}),
  },
  isSavingAll: {
    type: Boolean,
    default: false,
  },
});

const store = useStore();
const { t } = useI18n();
const BASE_KEY = 'AILAB.ASSISTANT_V2.TEMPLATES';

const isAIGamer = computed(
  () => props.assistant?.config?.wlab_sidebar_key === 'ai_gamer'
);

const isLoading = ref(false);

const config = computed(() => store.getters['aiCloser/getConfig']);
const templateMode = computed(
  () => config.value?.prompt_sections?.template_mode || {}
);
const riskPolicy = computed(
  () => config.value?.prompt_sections?.risk_policy || {}
);

const blockedIntentsText = ref('');
const riskEnabled = ref(true);
const intentRiskTiers = ref({});
const actionByTier = ref({});

const INTENTS = [
  'deposit',
  'withdraw',
  'bonus_claim',
  'turnover',
  'kyc',
  'account_locked',
  'signup',
  'promo',
  'support',
  'entry',
];

const TIERS = ['L0', 'L1', 'L2', 'L3'];
const actionOptions = computed(() => [
  {
    value: 'inherit',
    label: t(`${BASE_KEY}.RISK_TIER.ACTION_OPTIONS.INHERIT`),
  },
  {
    value: 'auto_suggest',
    label: t(`${BASE_KEY}.RISK_TIER.ACTION_OPTIONS.AUTO_SUGGEST`),
  },
  {
    value: 'suggest',
    label: t(`${BASE_KEY}.RISK_TIER.ACTION_OPTIONS.SUGGEST_ONLY`),
  },
  {
    value: 'handoff',
    label: t(`${BASE_KEY}.RISK_TIER.ACTION_OPTIONS.HANDOFF`),
  },
]);

const defaultTierForIntent = intent => {
  switch (String(intent || '').trim()) {
    case 'deposit':
    case 'withdraw':
    case 'bonus_claim':
    case 'turnover':
      return 'L3';
    case 'kyc':
    case 'account_locked':
      return 'L2';
    case 'entry':
      return 'L0';
    default:
      return 'L1';
  }
};

const defaultActionByTier = () => ({
  L0: 'inherit',
  L1: 'inherit',
  L2: 'auto_suggest',
  L3: 'auto_suggest',
});

const syncBlockedIntentsText = () => {
  const raw = riskPolicy.value?.auto_reply_block_intents;
  const list = Array.isArray(raw)
    ? raw
    : raw
      ? String(raw).split(/[,\s]+/)
      : [];
  blockedIntentsText.value = list
    .map(s => String(s).trim())
    .filter(Boolean)
    .join(', ');
};

const syncRiskTierState = () => {
  const rp = riskPolicy.value || {};
  riskEnabled.value = rp?.enabled !== false; // default true

  const tiers =
    rp?.intent_risk_tiers && typeof rp.intent_risk_tiers === 'object'
      ? rp.intent_risk_tiers
      : {};
  const next = {};
  INTENTS.forEach(i => {
    const v = tiers?.[i];
    const t = String(v || '')
      .trim()
      .toUpperCase();
    next[i] = TIERS.includes(t) ? t : defaultTierForIntent(i);
  });
  intentRiskTiers.value = next;

  const abt =
    rp?.action_by_tier && typeof rp.action_by_tier === 'object'
      ? rp.action_by_tier
      : {};
  const base = defaultActionByTier();
  const merged = { ...base };
  TIERS.forEach(t => {
    const v = String(abt?.[t] || '')
      .trim()
      .toLowerCase();
    merged[t] = ['inherit', 'auto_suggest', 'suggest', 'handoff'].includes(v)
      ? v
      : base[t];
  });
  actionByTier.value = merged;
};

const local = computed(() => ({
  enabled: !!templateMode.value?.enabled,
  on_match: templateMode.value?.on_match || 'strict',
  on_no_match: templateMode.value?.on_no_match || 'fallback_ai',
  ask_clarify_th: templateMode.value?.ask_clarify_th || '',
  ask_clarify_en: templateMode.value?.ask_clarify_en || '',
  default_reply_th: templateMode.value?.default_reply_th || '',
  default_reply_en: templateMode.value?.default_reply_en || '',
  allowed_domains: Array.isArray(templateMode.value?.allowed_domains)
    ? templateMode.value?.allowed_domains?.join(', ')
    : templateMode.value?.allowed_domains || '',
  match_distance_threshold:
    templateMode.value?.match_distance_threshold || 0.25,
  on_match_missing_question:
    templateMode.value?.on_match_missing_question || '',
}));

const onMatchCompatibilityLabel = computed(() => {
  if (local.value.on_match === 'strict_ask_missing') {
    return 'Legacy strict + ask missing';
  }
  return 'Legacy strict';
});

const fetchConfig = async () => {
  if (!props.assistant?.id) return;
  isLoading.value = true;
  try {
    await store.dispatch('aiCloser/fetchConfig', {
      assistantId: props.assistant.id,
    });
  } catch (error) {
    useAlert(error?.message || t(`${BASE_KEY}.ALERTS.LOAD_FAILED`));
  } finally {
    isLoading.value = false;
  }
};

onMounted(() => {
  if (isAIGamer.value) fetchConfig();
});

// Sync when config changes
watch(
  () => riskPolicy.value,
  () => {
    // Avoid overwriting local edits when other tabs update config during Save All.
    if (props.isSavingAll) return;
    syncBlockedIntentsText();
    syncRiskTierState();
  },
  { immediate: true, deep: true }
);

const updateTemplateMode = async patch => {
  if (!props.assistant?.id) return;
  try {
    const nextPromptSections = {
      ...(config.value?.prompt_sections || {}),
      template_mode: {
        ...local.value,
        ...patch,
      },
    };
    await store.dispatch('aiCloser/updateConfig', {
      assistantId: props.assistant.id,
      data: { prompt_sections: nextPromptSections },
    });
    useAlert(t('AILAB.ASSISTANT_V2.ALERTS.SAVED_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t(`${BASE_KEY}.ALERTS.SAVE_FAILED`));
  }
};

const updateRiskPolicy = async patch => {
  if (!props.assistant?.id) return;
  try {
    const nextPromptSections = {
      ...(config.value?.prompt_sections || {}),
      risk_policy: {
        ...(riskPolicy.value || {}),
        ...patch,
      },
    };
    await store.dispatch('aiCloser/updateConfig', {
      assistantId: props.assistant.id,
      data: { prompt_sections: nextPromptSections },
    });
    useAlert(t('AILAB.ASSISTANT_V2.ALERTS.SAVED_SUCCESS'));
  } catch (error) {
    useAlert(error?.message || t(`${BASE_KEY}.ALERTS.SAVE_FAILED`));
  }
};
</script>

<template>
  <div v-if="!isAIGamer" class="templates-tab">
    <div class="tab-header">
      <div class="header-left">
        <div class="header-icon">
          <span class="i-lucide-notebook-pen size-5" />
        </div>
        <div>
          <h1 class="header-title">{{ t(`${BASE_KEY}.HEADER.TITLE`) }}</h1>
          <p class="header-description">
            {{ t(`${BASE_KEY}.HEADER.DESCRIPTION_NON_AIGAMER`) }}
          </p>
        </div>
      </div>
    </div>
  </div>

  <div v-else class="templates-tab">
    <div class="tab-header">
      <div class="header-left">
        <div class="header-icon">
          <span class="i-lucide-notebook-pen size-5" />
        </div>
        <div>
          <h1 class="header-title">{{ t(`${BASE_KEY}.HEADER.TITLE`) }}</h1>
          <p class="header-description">
            {{ t(`${BASE_KEY}.HEADER.DESCRIPTION_AIGAMER`) }}
          </p>
        </div>
      </div>
    </div>

    <div class="card mb-6">
      <div class="text-sm font-semibold text-slate-900">
        {{ t(`${BASE_KEY}.RISK_TIER.TITLE`) }}
      </div>
      <div class="text-xs text-slate-500 mt-1">
        {{ t(`${BASE_KEY}.RISK_TIER.DESCRIPTION.PREFIX`) }}
        <b>{{ t(`${BASE_KEY}.MODES.AUTO_REPLY`) }}</b
        >{{ t(`${BASE_KEY}.RISK_TIER.DESCRIPTION.MIDDLE`) }}
        <b>{{ t(`${BASE_KEY}.MODES.AUTO_SUGGEST`) }}</b>
        {{ t(`${BASE_KEY}.COMMON.OR`) }}
        <b>{{ t(`${BASE_KEY}.MODES.SUGGEST`) }}</b
        >.
      </div>

      <div class="mt-3 flex items-center justify-between gap-3">
        <label class="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            class="h-4 w-4"
            :checked="riskEnabled"
            :disabled="isLoading"
            @change="updateRiskPolicy({ enabled: $event.target.checked })"
          />
          <span class="text-sm text-slate-900">{{
            t(`${BASE_KEY}.COMMON.ENABLED`)
          }}</span>
        </label>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <div class="text-xs font-semibold text-slate-700">
            {{ t(`${BASE_KEY}.RISK_TIER.INTENT_TO_TIER.TITLE`) }}
          </div>
          <div class="text-xs text-slate-500 mt-1">
            {{ t(`${BASE_KEY}.RISK_TIER.INTENT_TO_TIER.DESCRIPTION`) }}
          </div>
          <div class="mt-3 grid grid-cols-1 gap-2">
            <div
              v-for="i in INTENTS"
              :key="i"
              class="flex items-center justify-between gap-3 rounded-md border border-slate-200 dark:border-white/10 px-3 py-2"
            >
              <div class="text-sm text-slate-900 flex-1 pr-3 truncate">
                {{ i }}
              </div>
              <select
                class="w-1/2 min-w-[180px] rounded-md bg-white border border-slate-200 px-2 py-1 text-sm text-slate-900 text-right"
                :value="intentRiskTiers[i]"
                :disabled="isLoading || !riskEnabled"
                @change="
                  updateRiskPolicy({
                    intent_risk_tiers: {
                      ...(intentRiskTiers || {}),
                      [i]: $event.target.value,
                    },
                  })
                "
              >
                <option v-for="t in TIERS" :key="t" :value="t">{{ t }}</option>
              </select>
            </div>
          </div>
        </div>

        <div>
          <div class="text-xs font-semibold text-slate-700">
            {{ t(`${BASE_KEY}.RISK_TIER.TIER_TO_ACTION.TITLE`) }}
          </div>
          <div class="text-xs text-slate-500 mt-1">
            {{ t(`${BASE_KEY}.RISK_TIER.TIER_TO_ACTION.DESCRIPTION.PREFIX`) }}
            <b>{{ t(`${BASE_KEY}.MODES.AUTO_REPLY`) }}</b
            >{{ t(`${BASE_KEY}.RISK_TIER.TIER_TO_ACTION.DESCRIPTION.SUFFIX`) }}
          </div>

          <div class="mt-3 grid grid-cols-1 gap-2">
            <div
              v-for="t in TIERS"
              :key="t"
              class="flex items-center justify-between gap-3 rounded-md border border-slate-200 dark:border-white/10 px-3 py-2"
            >
              <div class="text-sm text-slate-900 flex-1 pr-3 truncate">
                {{ t }}
              </div>
              <select
                class="w-1/2 min-w-[180px] rounded-md bg-white border border-slate-200 px-2 py-1 text-sm text-slate-900 text-right"
                :value="actionByTier[t]"
                :disabled="isLoading || !riskEnabled"
                @change="
                  updateRiskPolicy({
                    action_by_tier: {
                      ...(actionByTier || {}),
                      [t]: $event.target.value,
                    },
                  })
                "
              >
                <option
                  v-for="a in actionOptions"
                  :key="a.value"
                  :value="a.value"
                >
                  {{ a.label }}
                </option>
              </select>
            </div>
          </div>

          <div class="mt-4 text-xs text-slate-500 dark:text-white/50">
            {{ t(`${BASE_KEY}.RISK_TIER.EVENT_LOG_PREFIX`) }}
            <b>{{ 'risk_gate.tier_mode_downgrade' }}</b
            >.
          </div>
        </div>
      </div>

      <div class="mt-5 pt-4 border-t border-slate-200 dark:border-white/10">
        <div class="text-xs font-semibold text-slate-700 dark:text-white/70">
          {{ t(`${BASE_KEY}.LEGACY_BLOCKED_INTENTS.TITLE`) }}
        </div>
        <div class="text-xs text-slate-500 dark:text-white/50 mt-1">
          {{ t(`${BASE_KEY}.LEGACY_BLOCKED_INTENTS.DESCRIPTION.PREFIX`) }}
          <b>{{ 'L3' }}</b
          >.
        </div>
        <div class="mt-3">
          <label class="text-xs text-slate-600 dark:text-white/60">{{
            t(`${BASE_KEY}.LEGACY_BLOCKED_INTENTS.LABEL`)
          }}</label>
          <input
            v-model="blockedIntentsText"
            type="text"
            class="mt-1 w-full rounded-md bg-white dark:bg-black/20 border border-slate-200 dark:border-white/10 px-3 py-2 text-sm text-slate-900 dark:text-white"
            :placeholder="t(`${BASE_KEY}.LEGACY_BLOCKED_INTENTS.PLACEHOLDER`)"
            :disabled="isLoading"
            @change="
              updateRiskPolicy({
                auto_reply_block_intents: blockedIntentsText
                  .split(/[,\s]+/)
                  .map(s => s.trim())
                  .filter(Boolean),
              })
            "
          />
          <div class="text-xs text-slate-500 dark:text-white/50 mt-1">
            {{ t(`${BASE_KEY}.LEGACY_BLOCKED_INTENTS.SUPPORTED`) }}
          </div>
        </div>
      </div>
    </div>

    <div class="card mb-6">
      <div class="flex items-start justify-between gap-4">
        <div>
          <div class="text-sm font-semibold text-slate-900 dark:text-white">
            {{ t(`${BASE_KEY}.TEMPLATE_MODE.TITLE`) }}
          </div>
          <div class="text-xs text-slate-500 dark:text-white/50 mt-1">
            {{ t(`${BASE_KEY}.TEMPLATE_MODE.DESCRIPTION`) }}
          </div>
        </div>

        <label class="inline-flex items-center gap-2 cursor-pointer">
          <input
            type="checkbox"
            class="h-4 w-4"
            :checked="local.enabled"
            :disabled="isLoading"
            @change="updateTemplateMode({ enabled: $event.target.checked })"
          />
          <span class="text-sm text-slate-900 dark:text-white">{{
            t(`${BASE_KEY}.COMMON.ENABLED`)
          }}</span>
        </label>
      </div>

      <div class="grid grid-cols-1 md:grid-cols-2 gap-4 mt-4">
        <div>
          <label class="text-xs text-slate-600 dark:text-white/60">
            Legacy on-match behavior
          </label>
          <div
            class="mt-1 rounded-md border border-slate-200 dark:border-white/10 bg-slate-50 dark:bg-black/20 px-3 py-3"
          >
            <div class="text-sm font-medium text-slate-900 dark:text-white">
              {{ onMatchCompatibilityLabel }}
            </div>
            <div class="text-xs text-slate-500 dark:text-white/50 mt-1">
              Direct template replies are retired. Exact and semantic matches now
              defer to the full AI prompt path so canonical article knowledge can
              win. This field is shown only as a legacy compatibility summary.
            </div>
            <div
              v-if="local.on_match === 'strict_ask_missing' && local.on_match_missing_question"
              class="text-xs text-slate-500 dark:text-white/50 mt-2"
            >
              Stored legacy follow-up:
              <span class="font-medium text-slate-700 dark:text-white/80">
                {{ local.on_match_missing_question }}
              </span>
            </div>
          </div>
        </div>

        <div>
          <label class="text-xs text-slate-600 dark:text-white/60">{{
            t(`${BASE_KEY}.TEMPLATE_MODE.WHEN_NO_MATCH.LABEL`)
          }}</label>
          <select
            class="mt-1 w-full rounded-md bg-white dark:bg-black/20 border border-slate-200 dark:border-white/10 px-3 py-2 text-sm text-slate-900 dark:text-white"
            :value="local.on_no_match"
            :disabled="!local.enabled || isLoading"
            @change="updateTemplateMode({ on_no_match: $event.target.value })"
          >
            <option value="fallback_ai">
              {{
                t(`${BASE_KEY}.TEMPLATE_MODE.WHEN_NO_MATCH.OPTIONS.FALLBACK_AI`)
              }}
            </option>
            <option value="ask_clarify">
              {{
                t(`${BASE_KEY}.TEMPLATE_MODE.WHEN_NO_MATCH.OPTIONS.ASK_CLARIFY`)
              }}
            </option>
            <option value="default_reply">
              {{
                t(
                  `${BASE_KEY}.TEMPLATE_MODE.WHEN_NO_MATCH.OPTIONS.DEFAULT_REPLY`
                )
              }}
            </option>
          </select>
          <div
            v-if="local.on_no_match === 'ask_clarify'"
            class="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2"
          >
            <textarea
              class="w-full rounded-md bg-white dark:!bg-black/20 border border-slate-200 dark:border-white/10 px-3 py-2 text-sm text-slate-900 dark:!text-white dark:placeholder-white/40"
              rows="2"
              :placeholder="
                t(
                  `${BASE_KEY}.TEMPLATE_MODE.WHEN_NO_MATCH.PLACEHOLDERS.ASK_CLARIFY_TH`
                )
              "
              :value="local.ask_clarify_th"
              :disabled="!local.enabled || isLoading"
              @change="
                updateTemplateMode({ ask_clarify_th: $event.target.value })
              "
            />
            <textarea
              class="w-full rounded-md bg-white dark:!bg-black/20 border border-slate-200 dark:border-white/10 px-3 py-2 text-sm text-slate-900 dark:!text-white dark:placeholder-white/40"
              rows="2"
              :placeholder="
                t(
                  `${BASE_KEY}.TEMPLATE_MODE.WHEN_NO_MATCH.PLACEHOLDERS.ASK_CLARIFY_EN`
                )
              "
              :value="local.ask_clarify_en"
              :disabled="!local.enabled || isLoading"
              @change="
                updateTemplateMode({ ask_clarify_en: $event.target.value })
              "
            />
          </div>
          <div
            v-if="local.on_no_match === 'default_reply'"
            class="mt-2 grid grid-cols-1 md:grid-cols-2 gap-2"
          >
            <textarea
              class="w-full rounded-md bg-white dark:!bg-black/20 border border-slate-200 dark:border-white/10 px-3 py-2 text-sm text-slate-900 dark:!text-white dark:placeholder-white/40"
              rows="2"
              :placeholder="
                t(
                  `${BASE_KEY}.TEMPLATE_MODE.WHEN_NO_MATCH.PLACEHOLDERS.DEFAULT_REPLY_TH`
                )
              "
              :value="local.default_reply_th"
              :disabled="!local.enabled || isLoading"
              @change="
                updateTemplateMode({ default_reply_th: $event.target.value })
              "
            />
            <textarea
              class="w-full rounded-md bg-white dark:!bg-black/20 border border-slate-200 dark:border-white/10 px-3 py-2 text-sm text-slate-900 dark:!text-white dark:placeholder-white/40"
              rows="2"
              :placeholder="
                t(
                  `${BASE_KEY}.TEMPLATE_MODE.WHEN_NO_MATCH.PLACEHOLDERS.DEFAULT_REPLY_EN`
                )
              "
              :value="local.default_reply_en"
              :disabled="!local.enabled || isLoading"
              @change="
                updateTemplateMode({ default_reply_en: $event.target.value })
              "
            />
          </div>
        </div>
      </div>

      <div class="mt-4">
        <label class="text-xs text-slate-600 dark:text-white/60">{{
          t(`${BASE_KEY}.TEMPLATE_MODE.MATCH_THRESHOLD.LABEL`)
        }}</label>
        <div class="flex items-center gap-3 mt-1">
          <input
            type="number"
            step="0.01"
            min="0.05"
            max="1"
            class="w-32 rounded-md bg-white dark:bg-black/20 border border-slate-200 dark:border-white/10 px-3 py-2 text-sm text-slate-900 dark:text-white"
            :value="local.match_distance_threshold"
            :disabled="!local.enabled || isLoading"
            @change="
              updateTemplateMode({
                match_distance_threshold: Number($event.target.value),
              })
            "
          />
          <div class="text-xs text-slate-500 dark:text-white/50">
            {{ t(`${BASE_KEY}.TEMPLATE_MODE.MATCH_THRESHOLD.RECOMMENDED`) }}
          </div>
        </div>
      </div>

      <div class="mt-4">
        <label class="text-xs text-slate-600 dark:text-white/60">{{
          t(`${BASE_KEY}.TEMPLATE_MODE.ALLOWED_DOMAINS.LABEL`)
        }}</label>
        <input
          type="text"
          class="mt-1 w-full rounded-md bg-white dark:bg-black/20 border border-slate-200 dark:border-white/10 px-3 py-2 text-sm text-slate-900 dark:text-white"
          :placeholder="
            t(`${BASE_KEY}.TEMPLATE_MODE.ALLOWED_DOMAINS.PLACEHOLDER`)
          "
          :value="local.allowed_domains"
          :disabled="!local.enabled || isLoading"
          @change="updateTemplateMode({ allowed_domains: $event.target.value })"
        />
        <div class="text-xs text-slate-500 dark:text-white/50 mt-1">
          {{ t(`${BASE_KEY}.TEMPLATE_MODE.ALLOWED_DOMAINS.RULE`) }}
        </div>
        <div
          v-if="local.enabled && !String(local.allowed_domains || '').trim()"
          class="mt-2 text-xs text-amber-600 dark:text-amber-400"
        >
          {{ t(`${BASE_KEY}.TEMPLATE_MODE.ALLOWED_DOMAINS.WARNING_EMPTY`) }}
        </div>
      </div>
    </div>

    <AnswerTemplateTab :assistant-id="assistant.id" :product="{}" />
  </div>
</template>

<style scoped>
.templates-tab {
  display: flex;
  flex-direction: column;
  gap: 24px;
  width: 100%;
  max-width: none;
  padding-bottom: 80px;
}

.tab-header {
  display: flex;
  align-items: flex-start;
  justify-content: space-between;
  gap: 20px;
  padding: 24px;
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  margin-bottom: 24px;
}

:global(body:not(.dark) .tab-header) {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
}

.header-left {
  display: flex;
  gap: 16px;
}

.header-icon {
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
  background: rgba(236, 72, 153, 0.15);
  border-radius: 10px;
  color: #f472b6;
  flex-shrink: 0;
}

.header-title {
  font-size: 18px;
  font-weight: 600;
  color: #e5e7eb;
  margin: 0;
}

:global(body:not(.dark) .header-title) {
  color: #1f2937;
}

.header-description {
  font-size: 14px;
  color: #a8aaae;
  margin: 4px 0 0 0;
}

:global(body:not(.dark) .header-description) {
  color: #6b7280;
}

.card {
  background: rgba(255, 255, 255, 0.03);
  border: 1px solid rgba(255, 255, 255, 0.08);
  border-radius: 12px;
  padding: 20px;
}

:global(body:not(.dark) .card) {
  background: #ffffff;
  border: 1px solid rgba(0, 0, 0, 0.08);
}
</style>
